/**
 * Plugin Interface — extension points for user code injection
 */

import type { TaskContext, TaskOutcome, AgentContractLike, TaskContractLike, PromptBuilderOptions } from "./task-runner.js";
import type { WorkflowResult } from "./workflow-runner.js";

export interface GuardrailResult {
  matched: boolean;
  guardrail_id: string;
  severity: "critical" | "mandatory" | "warning" | "info";
  action: "block" | "warn" | "shadow" | "info";
  message: string;
}

export interface PromptBuilderArgs {
  agent: AgentContractLike;
  task: TaskContractLike;
  context: TaskContext;
  options?: PromptBuilderOptions;
}

export interface AgentPlugin {
  readonly id: string;
  beforeTask?(taskId: string, context: TaskContext): Promise<TaskContext | null>;
  afterTask?(taskId: string, outcome: TaskOutcome): Promise<TaskOutcome>;
  /**
   * Enrich the structured TaskContext before prompt generation.
   * Unlike beforeTask (which can skip a task by returning null),
   * this hook is purely for context augmentation.
   */
  contextEnhancer?(taskId: string, context: TaskContext): TaskContext;
  /**
   * Lightweight prompt post-processor. Applied after the prompt is built.
   * Use for appending instructions or modifying sections.
   */
  promptEnhancer?(taskId: string, prompt: string, context: TaskContext): string;
  /**
   * Full prompt builder override. If provided and returns a non-null string,
   * replaces the default buildTaskPrompt output entirely.
   * Receives the agent/task contracts and context so the plugin can build
   * a completely custom prompt.
   */
  promptBuilder?(args: PromptBuilderArgs): string | null;
  customGuardrails?: {
    evaluateCommand?(command: string): GuardrailResult[];
    evaluateFilePath?(filePath: string): GuardrailResult[];
    evaluateFileContent?(filePath: string, content: string): GuardrailResult[];
  };
  beforeWorkflow?(workflowId: string, userRequest: string): Promise<void>;
  afterWorkflow?(workflowId: string, result: WorkflowResult): Promise<void>;
}

export class PluginRegistry {
  private plugins: AgentPlugin[] = [];

  register(plugin: AgentPlugin): void {
    if (this.plugins.some((p) => p.id === plugin.id)) {
      throw new Error(`Plugin "${plugin.id}" is already registered`);
    }
    this.plugins.push(plugin);
  }

  getAll(): readonly AgentPlugin[] {
    return this.plugins;
  }

  async runBeforeTask(taskId: string, context: TaskContext): Promise<TaskContext | null> {
    let ctx: TaskContext | null = context;
    for (const plugin of this.plugins) {
      if (!plugin.beforeTask || ctx === null) continue;
      ctx = await plugin.beforeTask(taskId, ctx);
    }
    return ctx;
  }

  async runAfterTask(taskId: string, outcome: TaskOutcome): Promise<TaskOutcome> {
    let result = outcome;
    for (const plugin of this.plugins) {
      if (!plugin.afterTask) continue;
      result = await plugin.afterTask(taskId, result);
    }
    return result;
  }

  applyContextEnhancers(taskId: string, context: TaskContext): TaskContext {
    let ctx = context;
    for (const plugin of this.plugins) {
      if (!plugin.contextEnhancer) continue;
      ctx = plugin.contextEnhancer(taskId, ctx);
    }
    return ctx;
  }

  /**
   * Try plugin promptBuilder hooks. Returns custom prompt if any plugin provides one,
   * or null to use the default buildTaskPrompt.
   */
  applyPromptBuilder(args: PromptBuilderArgs): string | null {
    for (const plugin of this.plugins) {
      if (!plugin.promptBuilder) continue;
      const result = plugin.promptBuilder(args);
      if (result !== null) return result;
    }
    return null;
  }

  applyPromptEnhancers(taskId: string, prompt: string, context: TaskContext): string {
    let enhanced = prompt;
    for (const plugin of this.plugins) {
      if (!plugin.promptEnhancer) continue;
      enhanced = plugin.promptEnhancer(taskId, enhanced, context);
    }
    return enhanced;
  }

  evaluateCommandGuardrails(command: string): GuardrailResult[] {
    const results: GuardrailResult[] = [];
    for (const plugin of this.plugins) {
      if (!plugin.customGuardrails?.evaluateCommand) continue;
      results.push(...plugin.customGuardrails.evaluateCommand(command));
    }
    return results;
  }

  evaluateFilePathGuardrails(filePath: string): GuardrailResult[] {
    const results: GuardrailResult[] = [];
    for (const plugin of this.plugins) {
      if (!plugin.customGuardrails?.evaluateFilePath) continue;
      results.push(...plugin.customGuardrails.evaluateFilePath(filePath));
    }
    return results;
  }

  evaluateFileContentGuardrails(filePath: string, content: string): GuardrailResult[] {
    const results: GuardrailResult[] = [];
    for (const plugin of this.plugins) {
      if (!plugin.customGuardrails?.evaluateFileContent) continue;
      results.push(...plugin.customGuardrails.evaluateFileContent(filePath, content));
    }
    return results;
  }

  async runBeforeWorkflow(workflowId: string, userRequest: string): Promise<void> {
    for (const plugin of this.plugins) {
      if (!plugin.beforeWorkflow) continue;
      await plugin.beforeWorkflow(workflowId, userRequest);
    }
  }

  async runAfterWorkflow(workflowId: string, result: WorkflowResult): Promise<void> {
    for (const plugin of this.plugins) {
      if (!plugin.afterWorkflow) continue;
      await plugin.afterWorkflow(workflowId, result);
    }
  }
}

export const pluginRegistry = new PluginRegistry();
